import { useEffect, useRef, useState } from "react";
import { ENTRY, WELCOME } from "@/villa142/config";
import { clientAuth, brandLogoUrl } from "@/villa142/api";

export const BrandMark = ({ brand, size = "small" }) =>
  brand?.has_logo ? (
    <img
      src={brandLogoUrl(brand.updated_at)}
      alt={brand.alt || "Ōraṇi"}
      className={`v142-brandmark is-${size}`}
      data-testid="v142-brand-logo"
    />
  ) : (
    <span className={`v142-brandmark-text is-${size}`} data-testid="v142-brand-text" aria-label={brand?.alt || "Ōraṇi"}>
      ORANI
    </span>
  );

export const SavedIndicator = ({ state }) => {
  if (state === "idle") return null;
  const text =
    state === "saving" ? "Saving…" :
    state === "pending" ? "Kept on this device — saving shortly" :
    "Saved";
  return (
    <span className={`v142-saved${state === "pending" ? " is-pending" : ""}`} data-testid="v142-saved-indicator" aria-live="polite">
      {text}
    </span>
  );
};

export const ResponderPicker = ({ people, responder, onPick }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sheetRef = useRef(null);
  const current = people.list.find((c) => c.id === responder);

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    const focusTarget = sheetRef.current?.querySelector("[data-autofocus]") || sheetRef.current?.querySelector("[role='radio']");
    focusTarget?.focus();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!confirm) return undefined;
    const t = setTimeout(() => setConfirm(null), 3200);
    return () => clearTimeout(t);
  }, [confirm]);

  const pick = async (c) => {
    if (busy) return;
    setBusy(true);
    setError("");
    const res = (await onPick(c.id)) || { ok: true };
    setBusy(false);
    if (!res.ok) {
      setError(
        res.reason === "conflict"
          ? "This reflection was updated elsewhere. Review the latest version on the question before switching."
          : "We couldn't save the current reflection just now. Check your connection and try again — nothing has been lost."
      );
      return;
    }
    setOpen(false);
    setConfirm(`New reflections will be added as ${c.name}.`);
  };

  return (
    <div className="v142-responder">
      <button
        type="button"
        className="v142-responder-chip"
        data-testid="v142-responder-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {current ? current.name : "Who is responding?"}
        <span className="v142-chev" aria-hidden="true">▾</span>
      </button>
      {confirm && !open && (
        <span className="v142-responder-confirm" role="status" aria-live="polite" data-testid="v142-responder-confirm">
          {confirm}
        </span>
      )}
      {open && (
        <>
          <div className="v142-responder-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            ref={sheetRef}
            className="v142-responder-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Who is responding?"
            data-testid="v142-responder-menu"
          >
            <span className="v142-sheet-handle" aria-hidden="true" />
            <div className="v142-sheet-head">
              <h3 className="v142-sheet-title">Who is responding?</h3>
              <button
                type="button"
                className="v142-sheet-close"
                data-testid="v142-responder-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="v142-responder-note">
              Choose who is responding now. This opens their reflection for this question; existing reflections will not change.
            </p>
            {error && (
              <p className="v142-responder-error" role="alert" data-testid="v142-responder-error">{error}</p>
            )}
            <div role="radiogroup" aria-label="Who is responding?" aria-busy={busy}>
              {people.list.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={responder === c.id}
                  data-autofocus={responder === c.id || undefined}
                  className={`v142-responder-item${responder === c.id ? " is-active" : ""}`}
                  data-testid={`v142-responder-${c.id}`}
                  disabled={busy}
                  onClick={() => pick(c)}
                >
                  <span className="v142-radio" aria-hidden="true" />
                  <span className="v142-responder-label">{c.name}</span>
                </button>
              ))}
            </div>
            {busy && <p className="v142-responder-note v142-responder-saving" aria-live="polite">Saving the current reflection…</p>}
          </div>
        </>
      )}
    </div>
  );
};

export const Gate = ({ brand, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [wrong, setWrong] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const enter = async (e) => {
    e.preventDefault();
    if (!password.trim() || busy) return;
    setBusy(true);
    setWrong(false);
    try {
      await clientAuth(password);
      onSuccess();
    } catch {
      setWrong(true);
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  return (
    <div className="v142 v142-stage v142-gate" data-testid="v142-gate">
      <form className="v142-gate-inner" onSubmit={enter}>
        {brand?.has_logo ? (
          <img
            src={brandLogoUrl(brand.updated_at)}
            alt={brand.alt || "Ōraṇi"}
            className="v142-gate-wordmark-img"
            data-testid="v142-brand-logo"
          />
        ) : (
          <span className="v142-gate-wordmark" data-testid="v142-brand-wordmark" aria-label="Ōraṇi">
            Ōraṇi
          </span>
        )}
        <h1 className="v142-display v142-gate-title">{ENTRY.heading}</h1>
        <p className="v142-gate-recipients" data-testid="v142-gate-recipients">Shruti &amp; Aditya</p>
        <p className="v142-copy v142-gate-note">{ENTRY.note}</p>
        <label htmlFor="v142-password" className="v142-gate-label">Password</label>
        <div className="v142-gate-fields">
          <input
            id="v142-password"
            ref={inputRef}
            data-testid="v142-password-input"
            className={`v142-input v142-gate-input${wrong ? " is-error" : ""}`}
            type="password"
            autoComplete="current-password"
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onInput={() => { if (wrong) setWrong(false); }}
            placeholder="Enter password"
            aria-invalid={wrong}
            aria-describedby="v142-gate-help"
          />
          <div className="v142-gate-hint" id="v142-gate-help">
            {wrong ? (
              <p className="v142-gate-hint-text v142-gate-error-text" role="alert" data-testid="v142-gate-error">
                Not quite. Say it like a Lemming.
              </p>
            ) : hintOpen ? (
              <p className="v142-gate-hint-text" data-testid="v142-gate-hint-text">Say it like a Lemming.</p>
            ) : (
              <button type="button" className="v142-gate-hint-btn" data-testid="v142-gate-hint-btn" onClick={() => setHintOpen(true)}>
                Need a hint?
              </button>
            )}
          </div>
          <button className="v142-btn v142-gate-btn" data-testid="v142-enter-btn" type="submit" disabled={busy}>
            {busy ? "One moment…" : "Continue"}
          </button>
        </div>
        <p className="v142-privacy">{ENTRY.privacy}</p>
      </form>
    </div>
  );
};

const useMedia = (query) => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [query]);
  return m;
};

const WelcomeImage = ({ urls }) => (
  <picture className="v142-reflect-bg" aria-hidden="true">
    {urls.mobile_avif && <source type="image/avif" media="(max-width: 640px)" srcSet={urls.mobile_avif} sizes="100vw" />}
    {urls.mobile_webp && <source type="image/webp" media="(max-width: 640px)" srcSet={urls.mobile_webp} sizes="100vw" />}
    {urls.desktop_avif && <source type="image/avif" srcSet={urls.desktop_avif} sizes="100vw" />}
    <img
      className="v142-reflect-img"
      src={urls.desktop_webp}
      alt=""
      aria-hidden="true"
      decoding="async"
      loading="eager"
      fetchPriority="high"
      sizes="100vw"
      data-testid="v142-welcome-image"
    />
  </picture>
);

const WelcomeVideo = ({ urls }) => {
  const mobile = useMedia("(max-width: 640px)");
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const saveData = typeof navigator !== "undefined" && navigator.connection?.saveData === true;
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);
  const src = mobile ? urls.mp4_mobile : urls.mp4_desktop;
  const webm = mobile ? urls.webm_mobile : urls.webm_desktop;
  const showVideo = Boolean(src) && !reduced && !saveData && !failed;

  useEffect(() => {
    const el = ref.current;
    if (!el || !showVideo) return undefined;
    el.muted = true;
    el.defaultMuted = true;
    const p = el.play();
    if (p && p.catch) p.catch(() => setFailed(true));
    return undefined;
  }, [showVideo, src]);

  return (
    <>
      <picture className="v142-reflect-bg" aria-hidden="true">
        {urls.poster_mobile && <source media="(max-width: 640px)" srcSet={urls.poster_mobile} sizes="100vw" />}
        <img
          className="v142-reflect-img"
          src={urls.poster_desktop}
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="eager"
          fetchPriority="high"
          sizes="100vw"
          data-testid="v142-welcome-poster"
        />
      </picture>
      {showVideo && (
        <video
          key={src}
          ref={ref}
          className={`v142-reflect-video${ready ? " is-ready" : ""}`}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          disablePictureInPicture
          disableRemotePlayback
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => setReady(true)}
          onError={() => setFailed(true)}
          data-testid="v142-welcome-video"
          data-ready={ready ? "true" : "false"}
        >
          {webm && <source src={webm} type="video/webm" />}
          <source src={src} type="video/mp4" />
        </video>
      )}
    </>
  );
};

export const MediaStage = ({ media, testId, contentTestId, children }) => {
  const kind = media?.kind;
  return (
    <div className={`v142 v142-stage v142-reflect${kind ? "" : " is-plain"}`} data-testid={testId} data-media={kind || "none"}>
      {kind === "image" && <WelcomeImage urls={media.urls} />}
      {kind === "video" && <WelcomeVideo urls={media.urls} />}
      {kind && <div className="v142-reflect-scrim" aria-hidden="true" />}
      <span className="v142-reflect-mark" data-testid="v142-reflect-mark">Villa 142</span>
      <section className="v142-reflect-content" data-testid={contentTestId}>{children}</section>
    </div>
  );
};

export const Welcome = ({ media, onBegin }) => (
  <MediaStage media={media} testId="v142-welcome-root" contentTestId="v142-welcome">
    <h1 className="v142-display v142-reflect-title">{WELCOME.heading}</h1>
    <p className="v142-reflect-copy">{WELCOME.copy1}</p>
    <p className="v142-reflect-copy">{WELCOME.copy2}</p>
    <button className="v142-reflect-btn" data-testid="v142-begin-btn" onClick={onBegin}>{WELCOME.button}</button>
  </MediaStage>
);

export const EndingScreen = ({ media, names, onReturn }) => (
  <MediaStage media={media} testId="v142-ending-root" contentTestId="v142-thanks">
    <h1 className="v142-display v142-reflect-title" data-testid="v142-ending-heading">Thank you{names ? `, ${names}` : ""}.</h1>
    <p className="v142-reflect-copy" data-testid="v142-thanks-copy">Thank you for letting us into a little more of your world. We’ll carry these reflections with us as we continue shaping Villa 142 together.</p>
    <button className="v142-reflect-btn v142-reflect-btn-quiet" data-testid="v142-return-reflections-btn" onClick={onReturn}>
      Return to your reflections
    </button>
  </MediaStage>
);

export const ContributorChoice = ({ people, current, onChoose, onContinueFrom, onChooseWhere }) => {
  const [sel, setSel] = useState(current || null);
  return (
    <section className="v142-pause" data-testid="v142-contributor-screen">
      <h1 className="v142-display">Who is responding today?</h1>
      <p className="v142-copy v142-pause-copy">
        You can change this at any time — and every question welcomes more than one perspective.
      </p>
      <div className="v142-contrib-row" role="radiogroup" aria-label="Who is responding today?">
        {people.list.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={sel === c.id}
            className={`v142-contrib${sel === c.id ? " is-active" : ""}`}
            data-testid={`v142-contributor-${c.id}`}
            onClick={() => { setSel(c.id); onChoose(c.id); }}
          >
            {c.name}
          </button>
        ))}
      </div>
      {sel && (
        <div className="v142-confirm-actions v142-begin-actions">
          <button className="v142-btn" data-testid="v142-continue-from-btn" onClick={() => onContinueFrom(sel)}>
            Continue from your reflections
          </button>
          <button className="v142-ghost" data-testid="v142-choose-where-btn" onClick={() => onChooseWhere(sel)}>
            Choose where to begin
          </button>
        </div>
      )}
    </section>
  );
};

export const ChapterPause = ({ chapter, chapterIndex, onContinue }) => (
  <section className={`v142-pause v142-pausepage v142-tone-${chapterIndex % 3}`} data-testid={`v142-pause-${chapter.id}`}>
    <p className="v142-eyebrow">{chapter.title}</p>
    <h1 className="v142-pause-line">{chapter.intro}</h1>
    <span className="v142-rule" aria-hidden="true" />
    <button type="button" className="v142-btn v142-nav-ctl" data-testid="v142-continue-btn" onClick={onContinue}>Continue</button>
  </section>
);

export const ChapterEnd = ({ chapter, nextTitle, onContinue, onChoose, onSaveLeave }) => (
  <section className="v142-pause" data-testid={`v142-chend-${chapter.id}`}>
    <p className="v142-eyebrow">{chapter.title}</p>
    <h1 className="v142-display">That's the {chapter.title} chapter.</h1>
    <p className="v142-copy v142-pause-copy">
      Anything you left open stays part of the conversation — you can return to it whenever a thought arises.
    </p>
    <div className="v142-confirm-actions">
      <button type="button" className="v142-btn v142-nav-ctl" data-testid="v142-chend-continue" onClick={onContinue}>
        Continue to {nextTitle}
      </button>
    </div>
    <div className="v142-chend-secondary">
      <button type="button" className="v142-link" data-testid="v142-chend-choose" onClick={onChoose}>Choose another reflection</button>
      <button type="button" className="v142-link" data-testid="v142-chend-save-leave" onClick={onSaveLeave}>Save and leave</button>
    </div>
  </section>
);

export const PausedScreen = ({ onResume }) => (
  <section className="v142-pause" data-testid="v142-paused">
    <h1 className="v142-display">Your reflections are saved.</h1>
    <p className="v142-copy v142-pause-copy">
      You can close this window and return whenever you like — everything will be exactly as you left it.
    </p>
    <button type="button" className="v142-btn v142-nav-ctl" data-testid="v142-resume-btn" onClick={onResume}>Return to the reflection</button>
  </section>
);
